import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";

const MONTHS = [
  { v: 1, l: "Jan" }, { v: 2, l: "Feb" }, { v: 3, l: "Mar" }, { v: 4, l: "Apr" },
  { v: 5, l: "May" }, { v: 6, l: "Jun" }, { v: 7, l: "Jul" }, { v: 8, l: "Aug" },
  { v: 9, l: "Sep" }, { v: 10, l: "Oct" }, { v: 11, l: "Nov" }, { v: 12, l: "Dec" },
];

const CURRENT_YEAR = new Date().getFullYear();

// Available years: every year from 2020 to the current year + 1, plus an "All time" option.
function buildYears() {
  const out = [];
  for (let y = CURRENT_YEAR + 1; y >= 2020; y -= 1) out.push(y);
  return out;
}

export default function GlobalDateFilter({ value, onChange }) {
  const years = buildYears();
  const year = value?.year ?? CURRENT_YEAR;
  const fromMonth = value?.fromMonth ?? null;
  const toMonth = value?.toMonth ?? null;
  const isDirty = year !== CURRENT_YEAR || fromMonth != null || toMonth != null;

  const set = (patch) => onChange({ year, fromMonth, toMonth, ...patch });

  const stepYear = (delta) => {
    if (year === "all") {
      set({ year: delta < 0 ? years[years.length - 1] : years[0] });
      return;
    }
    const idx = years.indexOf(year);
    const next = years[idx + delta];
    if (next != null) set({ year: next });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-white dark:bg-[var(--bg-surface)] border-b border-gray-100 dark:border-[var(--border-soft)]">
      <span className="text-[11px] uppercase tracking-wider text-brand-slate font-medium">Filter</span>

      <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 dark:border-[var(--border-soft)] bg-white dark:bg-[var(--bg-surface)] p-0.5">
        <button
          type="button"
          onClick={() => stepYear(1)}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate"
          title="Earlier year"
        >
          <ChevronLeft size={14} />
        </button>
        <select
          value={year}
          onChange={(e) => set({ year: e.target.value === "all" ? "all" : Number(e.target.value) })}
          className="px-2 py-0.5 bg-transparent text-xs font-medium text-brand-ink dark:text-[var(--text-primary)] focus:outline-none cursor-pointer"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
          <option value="all">All time</option>
        </select>
        <button
          type="button"
          onClick={() => stepYear(-1)}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate"
          title="Later year"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      <div className="flex items-center gap-1">
        <span className="text-[11px] text-brand-slate">From</span>
        <select
          value={fromMonth ?? 1}
          onChange={(e) => {
            const v = Number(e.target.value);
            // When user picks "From X" while "To" is empty, auto-set "To" to the
            // same month so the range becomes "just X" instead of "X onwards".
            // The user can still widen it manually. Matches what most people
            // mean when they pick a single month.
            const nextTo = toMonth == null ? v : toMonth;
            set({ fromMonth: v, toMonth: nextTo });
          }}
          className="px-2 py-1 border border-gray-200 dark:border-[var(--border-soft)] rounded text-xs bg-white dark:bg-[var(--bg-surface)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
        >
          {MONTHS.map((m) => (
            <option key={m.v} value={m.v}>{m.l}</option>
          ))}
        </select>
        <span className="text-[11px] text-brand-slate">To</span>
        <select
          value={toMonth ?? 12}
          onChange={(e) => set({ toMonth: Number(e.target.value) })}
          className="px-2 py-1 border border-gray-200 dark:border-[var(--border-soft)] rounded text-xs bg-white dark:bg-[var(--bg-surface)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
        >
          {MONTHS.map((m) => (
            <option key={m.v} value={m.v}>{m.l}</option>
          ))}
        </select>
      </div>

      {/* Resolved range badge — always visible so it's clear what's actually
          being shown. "Feb only", "Feb–May 2026", "Full year 2026", etc. */}
      <span className="px-2 py-0.5 rounded-full bg-brand-blue-soft text-brand-blue-dark text-[11px] font-medium">
        {year === "all"
          ? "All time"
          : (() => {
              const f = fromMonth ?? 1;
              const t = toMonth ?? 12;
              if (f === 1 && t === 12) return `Full year ${year}`;
              if (f === t) return `${MONTHS[f - 1].l} ${year}`;
              return `${MONTHS[f - 1].l}–${MONTHS[t - 1].l} ${year}`;
            })()}
      </span>

      {isDirty && (
        <button
          type="button"
          onClick={() => onChange({ year: CURRENT_YEAR, fromMonth: null, toMonth: null })}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-brand-slate hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] hover:text-brand-ink"
          title="Reset to current year, all months"
        >
          <RotateCcw size={11} /> Reset
        </button>
      )}
    </div>
  );
}

// Helper used by tabs to filter date strings (YYYY-MM-DD) by the global filter.
export function isDateInFilter(dateStr, filter) {
  if (!dateStr) return true;
  if (!filter) return true;
  const y = Number(dateStr.slice(0, 4));
  const m = Number(dateStr.slice(5, 7));
  if (filter.year !== "all" && filter.year != null && y !== filter.year) return false;
  if (filter.fromMonth != null && m < filter.fromMonth) return false;
  if (filter.toMonth != null && m > filter.toMonth) return false;
  return true;
}
